import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Table,
  Typography,
  Popconfirm,
  Tag,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Tabs,
  Row,
  Col,
  DatePicker,
  Drawer,
  Space,
  Tooltip,
  message,
} from 'antd';
import {
  PoweroffOutlined,
  EditOutlined,
  SearchOutlined,
  UserSwitchOutlined,
  FileExcelOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import type { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import UserService from '../../services/UserService';
import RoleService from '../../services/RoleService'; // ROLLERİ BURADAN GETİRECEĞİZ
import { extractApiErrorMessage, formatPhoneNumberCopy, openNotification } from '../../utils';       // openNotification fonksiyonu
// Use styled XLSX for bold headers
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import PhoneNumberDisplay from '../../components/PhoneNumberDisplay';
import AuthService from '../../services/AuthService';
import {
  APPLICATION_LOGIN_PERMISSION,
  SUPPORT_APP_BASE_URL,
  isApplicationLoginHost,
} from '../../config/applicationLogin';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const ALLOWED_CREATE_ROLE_NAMES = ['CENTRAL_USER', 'FINANCE', 'ADMIN'];
const DUPLICATE_EMAIL_MESSAGE = 'Bu e-posta adresi ile kayıtlı bir kullanıcı bulunmaktadır.';

type DateRangeValue = [Dayjs | null, Dayjs | null] | null;
type CreateUserFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  password: string;
  passwordRepeat: string;
  roleName: string;
};

const normalizeSearchTerm = (value?: string) => {
  if (!value) {
    return '';
  }

  try {
    return value.toLocaleLowerCase('tr-TR');
  } catch (error) {
    return value.toLowerCase();
  }
};

const normalizeCreateUserErrorMessage = (error: any, apiMessage: string) => {
  const errorCode = String(error?.response?.data?.errorCode ?? '');
  const normalizedSource = `${errorCode} ${apiMessage}`.toLocaleLowerCase('tr-TR');

  if (
    errorCode === 'account-already-exists' ||
    normalizedSource.includes('accountalreadyexistsexception') ||
    (normalizedSource.includes('email with') && normalizedSource.includes('already exists'))
  ) {
    return DUPLICATE_EMAIL_MESSAGE;
  }

  if (apiMessage.includes('unique_account_phone') || apiMessage.includes('Key (phone)=')) {
    return 'Telefon numarası daha önce kullanıldı.';
  }

  return apiMessage;
};

export const UserList = () => {
  const [createUserForm] = Form.useForm<CreateUserFormValues>();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activeTabKey, setActiveTabKey] = useState<'active' | 'inactive'>('active');
  const [activeCount, setActiveCount] = useState<number>(0);
  const [inactiveCount, setInactiveCount] = useState<number>(0);

  const [activeFilters, setActiveFilters] = useState<{
    role?: string;
    lastLoginRange: DateRangeValue;
    createdRange: DateRangeValue;
  }>({
    role: undefined,
    lastLoginRange: null,
    createdRange: null,
  });

  const [inactiveFilters, setInactiveFilters] = useState<{
    role?: string;
    deactivatedRange: DateRangeValue;
    lastLoginRange: DateRangeValue;
    createdRange: DateRangeValue;
  }>({
    role: undefined,
    deactivatedRange: null,
    lastLoginRange: null,
    createdRange: null,
  });

  // Default filters for quick reset on tab change
  const defaultActiveFilters: { role?: string; lastLoginRange: DateRangeValue; createdRange: DateRangeValue } = {
    role: undefined,
    lastLoginRange: null,
    createdRange: null,
  };
  const defaultInactiveFilters: { role?: string; deactivatedRange: DateRangeValue; lastLoginRange: DateRangeValue; createdRange: DateRangeValue } = {
    role: undefined,
    deactivatedRange: null,
    lastLoginRange: null,
    createdRange: null,
  };

  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [drawerTabKey, setDrawerTabKey] = useState<'active' | 'inactive'>('active');
  const [drawerFilters, setDrawerFilters] = useState<{
    role?: string;
    lastLoginRange: DateRangeValue;
    createdRange: DateRangeValue;
    deactivatedRange: DateRangeValue;
  }>({
    role: undefined,
    lastLoginRange: null,
    createdRange: null,
    deactivatedRange: null,
  });

  // Sort/sıralama
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortDirection, setSortDirection] = useState<string>('DESC');

  // Sayfalama
  const [pagination, setPagination] = useState<any>({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const currentPage = pagination.current;
  const currentPageSize = pagination.pageSize;

  // Router
  const navigate = useNavigate();

  // Rol değiştirme modal state'leri
  const [roleChangeModalVisible, setRoleChangeModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [newRole, setNewRole] = useState<string>('');
  const [deactivateModalVisible, setDeactivateModalVisible] = useState(false);
  const [deactivatingUser, setDeactivatingUser] = useState<any>(null);
  const [deactivateNoteText, setDeactivateNoteText] = useState<string>('');
  const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
  const [createSubmitting, setCreateSubmitting] = useState(false);

  // Sunucudan gelen tüm roller:
  const [allRoles, setAllRoles] = useState<any[]>([]);

  // **Giriş yapan kullanıcının** rolleri (Admin mi diye bakacağız)
  const [loggedInUserRoles, setLoggedInUserRoles] = useState<any[]>([]);
  const [loggedInUserPermissions, setLoggedInUserPermissions] = useState<string[]>([]);
  const [loginAccountId, setLoginAccountId] = useState<number | string | null>(null);

  const buildFilterParams = useCallback(
    (statusKey: 'active' | 'inactive') => {
      const params: Record<string, any> = {};
      const source = statusKey === 'active' ? activeFilters : inactiveFilters;

      if (source.role) {
        params.role = source.role;
      }

      const applyRange = (range: DateRangeValue, prefix: string) => {
        if (range && range[0] && range[1]) {
          // Send fixed UTC bounds regardless of client TZ
          const startIso = `${range[0].format('YYYY-MM-DD')}T00:00:00.000Z`;
          const endIso = `${range[1].format('YYYY-MM-DD')}T23:59:59.999Z`;
          params[`${prefix}Start`] = startIso;
          params[`${prefix}End`] = endIso;
        }
      };

      applyRange(source.lastLoginRange, 'lastLogin');
      applyRange(source.createdRange, 'created');

      if (statusKey === 'inactive') {
        const s = source as typeof inactiveFilters;
        applyRange(s.deactivatedRange, 'deactivated');
      }

      return params;
    },
    [activeFilters, inactiveFilters]
  );

  /**
   * Roller API'den çekilir ve state'e kaydedilir.
   * name === 'CUSTOMER' olan rol, ekranda "İş Ortağı" olarak gösterilecek.
   */
  const fetchRoles = async () => {
    try {
      const res = await RoleService.getAll();
      // Örnek response: res.data = [{ id:1, name:'CUSTOMER'}, {id:2, name:'ADMIN'}, ...]
      setAllRoles(res.data || []);
    } catch (error) {
      console.error(error);
      openNotification('error', 'Hata', 'Roller yüklenirken hata oluştu.');
    }
  };

  // Giriş yapan kullanıcının rollerini localStorage'dan oku
  const loadLoggedInUserRoles = () => {
    const storedRoles = localStorage.getItem('roles');
    if (storedRoles) {
      try {
        const parsed = JSON.parse(storedRoles);
        setLoggedInUserRoles(parsed);
      } catch (err) {
        console.error('Giriş yapan kullanıcının rolleri parse edilirken hata:', err);
      }
    }

    const storedPermissions = localStorage.getItem('permissions');
    if (storedPermissions) {
      try {
        const parsed = JSON.parse(storedPermissions);
        setLoggedInUserPermissions(Array.isArray(parsed) ? parsed : []);
      } catch {
        setLoggedInUserPermissions([]);
      }
    }
  };

  /**
   * Tablodaki kullanıcıları çekmek
   */
  const getUsers = useCallback(
    (
      search?: string,
    page = 1,
    pageSize = 50,
      sField = 'createdAt',
      sDirection = 'DESC',
      statusFilter?: boolean,
      extraFilters: Record<string, any> = {}
    ) => {
      setLoading(true);
      const normalizedSearch = normalizeSearchTerm(search);

      UserService.getAll({
        name: normalizedSearch || undefined,
        page: page - 1, // Spring vb. backend 0-based
        size: pageSize,
        sortField: sField,
        sortDirection: sDirection,
        status: statusFilter,
        ...extraFilters,
      })
        .then((response: any) => {
          setUsers(response.data.content);
          setPagination((prev: any) => ({
            ...prev,
            current: page,
            pageSize,
            total: response.data.totalElements,
          }));
          const total = response?.data?.totalElements ?? 0;
          if (statusFilter === true) {
            setActiveCount(total);
          } else if (statusFilter === false) {
            setInactiveCount(total);
          }
          setLoading(false);
        })
        .catch((e: any) => {
          console.error(e);
          setLoading(false);
          openNotification('error', 'Hata', 'Kullanıcılar yüklenirken hata oluştu.');
        });
    },
    []
  );

  // İlk yüklemelerde kullanıcılar + roller çekilir + giriş yapan userRoller
  useEffect(() => {
    loadLoggedInUserRoles();
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const statusFilter = activeTabKey === 'active';
    const currentFilters = buildFilterParams(activeTabKey);
    getUsers(
      searchTerm,
      currentPage,
      currentPageSize,
    sortField,
    sortDirection,
    statusFilter,
    currentFilters
    );
  }, [
    activeTabKey,
    buildFilterParams,
    getUsers,
    currentPage,
    currentPageSize,
    searchTerm,
    sortDirection,
    sortField,
  ]);

  // Kullanıcı statüsünü (aktif/pasif) değiştir
  const toggleUserStatus = async (id: number, isActive: boolean, noteText?: string) => {
    try {
      await UserService.toggleStatus(id, !isActive, noteText);
      openNotification('success', 'Başarılı', `Kullanıcı ${!isActive ? 'aktif' : 'pasif'} hale getirildi.`);
      const statusFilter = activeTabKey === 'active';
      const currentFilters = buildFilterParams(activeTabKey);
      getUsers(
        searchTerm,
        pagination.current,
        pagination.pageSize,
        sortField,
        sortDirection,
        statusFilter,
        currentFilters
      );
      // No extra request for opposite tab count on filter/apply
      return true;
    } catch (error) {
      console.error(error);
      openNotification('error', 'Hata', 'İşlem sırasında bir hata oluştu.');
      return false;
    }
  };

  const openDeactivateModal = (user: any) => {
    setDeactivatingUser(user);
    setDeactivateNoteText('');
    setDeactivateModalVisible(true);
  };

  const handleDeactivateCancel = () => {
    setDeactivateModalVisible(false);
    setDeactivatingUser(null);
    setDeactivateNoteText('');
  };

  const handleDeactivateConfirm = async () => {
    if (!deactivatingUser) return;
    if (!deactivateNoteText.trim()) {
      openNotification('warning', 'Uyarı', 'Lütfen pasifleştirme gerekçesini giriniz.');
      return;
    }

    const success = await toggleUserStatus(
      deactivatingUser.id,
      deactivatingUser.status,
      deactivateNoteText.trim()
    );
    if (success) {
      handleDeactivateCancel();
    }
  };

  // Aramayı debounce ile yönet
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        const normalizedValue = normalizeSearchTerm(value);
        setPagination((prev: any) => ({ ...prev, current: 1 }));
        setSearchTerm(normalizedValue);
      }, 300),
    []
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(event.target.value);
  };

  const updateFilters = (
    statusKey: 'active' | 'inactive',
    updates: Partial<{
      role?: string;
      lastLoginRange: DateRangeValue;
      createdRange: DateRangeValue;
      deactivatedRange: DateRangeValue;
    }>
  ) => {
    if (statusKey === 'active') {
      setActiveFilters((prev) => ({ ...prev, ...updates }));
    } else {
      setInactiveFilters((prev) => ({ ...prev, ...updates }));
    }
    setPagination((prev: any) => ({ ...prev, current: 1 }));
  };

  const openFilterDrawer = (statusKey: 'active' | 'inactive') => {
    setDrawerTabKey(statusKey);
    const source = statusKey === 'active' ? activeFilters : inactiveFilters;
    setDrawerFilters({
      role: source.role,
      lastLoginRange: source.lastLoginRange,
      createdRange: source.createdRange,
      deactivatedRange:
        statusKey === 'inactive'
          ? (source as typeof inactiveFilters).deactivatedRange ?? null
          : null,
    });
    setFilterDrawerVisible(true);
  };

  const closeFilterDrawer = () => setFilterDrawerVisible(false);

  const setDrawerRange = (
    field: 'lastLoginRange' | 'createdRange' | 'deactivatedRange',
    values: DateRangeValue
  ) => {
    const range = values && values[0] && values[1] ? values : null;
    setDrawerFilters((prev) => ({ ...prev, [field]: range }));
  };

  const applyDrawerFilters = () => {
    const updates: Partial<{
      role?: string;
      lastLoginRange: DateRangeValue;
      createdRange: DateRangeValue;
      deactivatedRange: DateRangeValue;
    }> = {
      role: drawerFilters.role,
      lastLoginRange: drawerFilters.lastLoginRange,
      createdRange: drawerFilters.createdRange,
    };
    if (drawerTabKey === 'inactive') {
      updates.deactivatedRange = drawerFilters.deactivatedRange;
    }
    updateFilters(drawerTabKey, updates);
    setFilterDrawerVisible(false);
  };

  const resetDrawerFilters = () => {
    const empty = {
      role: undefined,
      lastLoginRange: null as DateRangeValue,
      createdRange: null as DateRangeValue,
      deactivatedRange: null as DateRangeValue,
    };
    setDrawerFilters(empty);
    const updates = drawerTabKey === 'inactive'
      ? empty
      : { role: empty.role, lastLoginRange: empty.lastLoginRange, createdRange: empty.createdRange };
    updateFilters(drawerTabKey, updates as any);
  };

  const handleTabChange = (key: string) => {
    const tabKey = key as 'active' | 'inactive';
    setActiveTabKey(tabKey);
    setPagination((prev: any) => ({ ...prev, current: 1 }));
    // Reset search and filters when switching tabs
    setSearchTerm('');
    if (tabKey === 'active') {
      setActiveFilters({ ...defaultActiveFilters });
      setSortField('createdAt');
      setSortDirection('DESC');
    } else {
      setInactiveFilters({ ...defaultInactiveFilters });
      setSortField('deactivatedAt');
      setSortDirection('DESC');
    }
    // Close filter drawer when changing tab
    setFilterDrawerVisible(false);
  };

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Tablo sayfa/sort değişimlerini yönet
  const handleTableChange = (
    paginationConfig: TablePaginationConfig,
    _filters: any,
    sorter: any | any[]
  ) => {
    let sField = sortField;
    let sDirection = sortDirection;

    if (!Array.isArray(sorter) && sorter.field) {
      sField = sorter.field.toString();
      sDirection = sorter.order === 'ascend' ? 'ASC' : 'DESC';
    }

    setSortField(sField);
    setSortDirection(sDirection);

      setPagination({
        ...pagination,
        current: paginationConfig.current,
        pageSize: paginationConfig.pageSize || 50,
      });
  };

  // Kullanıcı detay sayfasına git
  const handleDetail = (id: string) => {
    navigate(`/users/${id}`);
  };

  // Excel dışa aktarım: mevcut arama ve filtrelerle tüm kullanıcıları çekip indirir
  const handleExportExcel = async (statusKey: 'active' | 'inactive') => {
    try {
      const statusFilter = statusKey === 'active';
      const filterParams = buildFilterParams(statusKey);
      const normalizedSearch = normalizeSearchTerm(searchTerm);
      const params = {
        name: normalizedSearch || undefined,
        status: statusFilter,
        ...filterParams,
      } as any;

      const response: any = await UserService.getUsersWithoutPagination(params);
      const raw = response?.data?.content ?? response?.data ?? [];

      const data = Array.isArray(raw) ? raw : [];
      // Prepare header with bold style similar to Authorized Persons export
      const headerRow: any[] = [
        { v: 'Ad', s: { font: { bold: true } } },
        { v: 'Soyad', s: { font: { bold: true } } },
        { v: 'Telefon', s: { font: { bold: true } } },
        { v: 'E-posta', s: { font: { bold: true } } },
      ];
      const rows = data.map((item: any) => [
        item.firstName ?? '',
        item.lastName ?? '',
        formatPhoneNumberCopy(item.phoneNumber) ?? '',
        item.email ?? '',
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Kullanıcılar');
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
      saveAs(blob, 'kullanicilar.xlsx');
    } catch (err) {
      console.error('Excel export error:', err);
      message.error('Excel indirirken hata oluştu');
    }
  };

  // "Rolü Değiştir" butonu
  const openRoleChangeModal = (user: any) => {
    setSelectedUser(user);
    if (user.roles && user.roles.length > 0) {
      setNewRole(user.roles[0].name);
    } else {
      setNewRole('CUSTOMER');
    }
    setRoleChangeModalVisible(true);
  };

  // "Rolü Değiştir" Kaydet
  const handleRoleChangeSave = async () => {
    if (!selectedUser) return;

    try {
      // allRoles içinden name=newRole olanı bulalım
      const selectedRoleObj = allRoles.find((r: any) => r.name === newRole);
      if (!selectedRoleObj) {
        openNotification('error', 'Hata', 'Seçilen rol geçersiz.');
        return;
      }
      // roleId bu objeden gelir
      const roleId = selectedRoleObj.id;

      await UserService.updateRole(selectedUser.id, { roleId });
      openNotification('success', 'Başarılı', 'Kullanıcının rolü başarıyla güncellendi.');
      setRoleChangeModalVisible(false);
      setSelectedUser(null);

      // Listeyi yenile
      const statusFilter = activeTabKey === 'active';
      const currentFilters = buildFilterParams(activeTabKey);
      getUsers(
        searchTerm,
        pagination.current,
        pagination.pageSize,
        sortField,
        sortDirection,
        statusFilter,
        currentFilters
      );
      // No extra request for opposite tab count on filter/apply
    } catch (error) {
      console.error(error);
      openNotification('error', 'Hata', 'Rol güncellenirken bir hata oluştu.');
    }
  };

  const handleRoleChangeCancel = () => {
    setRoleChangeModalVisible(false);
    setSelectedUser(null);
  };

  // "CUSTOMER" => "İş Ortağı", aksi halde role.name'i label
  const dynamicRoleOptions = allRoles.map((role) => ({
    value: role.name,
    label: role.title,
  }));
  const createRoleOptions = allRoles
    .filter((role) => ALLOWED_CREATE_ROLE_NAMES.includes(role?.name))
    .map((role) => ({
      value: role.name,
      label: role.title || role.name,
    }));

  // **Şu anki kullanıcı Admin mi?** Kontrolü
  const isCurrentUserAdmin = loggedInUserRoles.some((r) => r.name === 'ADMIN');
  const canLoginToApplication = loggedInUserPermissions.includes(APPLICATION_LOGIN_PERMISSION);

  const loginToApplication = async (accountId: number | string) => {
    const loginWindow = window.open('', '_blank');
    if (!loginWindow) {
      openNotification(
        'error',
        'Pencere Açılamadı',
        'Yeni sekme açılamadı. Lütfen tarayıcınızın pop-up engelleyicisini kontrol ediniz.'
      );
      return;
    }

    loginWindow.opener = null;
    setLoginAccountId(accountId);
    try {
      const response = await AuthService.loginByAccountId(accountId);
      const tokenId = response?.data?.tokenId;
      if (!tokenId) throw new Error('Token yanıtı eksik.');
      loginWindow.location.replace(`${SUPPORT_APP_BASE_URL}/login-by-token/${encodeURIComponent(tokenId)}`);
    } catch (error) {
      loginWindow.close();
      const errorMessage = (error as any)?.response
        ? extractApiErrorMessage(error, 'Uygulamaya giriş başlatılamadı.')
        : 'Uygulamaya giriş başlatılamadı.';
      openNotification('error', 'Başarısız', errorMessage);
    } finally {
      setLoginAccountId(null);
    }
  };

  const closeCreateDrawer = () => {
    setCreateDrawerVisible(false);
    createUserForm.resetFields();
  };

  const handleCreateUser = async (values: CreateUserFormValues) => {
    const selectedRole = allRoles.find((role) => role.name === values.roleName);
    if (!selectedRole?.id) {
      openNotification('error', 'Hata', 'Lütfen geçerli bir rol seçiniz.');
      return;
    }

    setCreateSubmitting(true);
    try {
      const response = await UserService.createAccount({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email.toLowerCase(),
        phone: values.phone,
        password: values.password,
        roleId: selectedRole.id,
      });

      if (!response?.data?.id) {
        throw new Error();
      }

      openNotification('success', 'Başarılı', 'Kullanıcı başarıyla eklendi.');
      closeCreateDrawer();

      const statusFilter = activeTabKey === 'active';
      const currentFilters = buildFilterParams(activeTabKey);
      getUsers(
        searchTerm,
        pagination.current,
        pagination.pageSize,
        sortField,
        sortDirection,
        statusFilter,
        currentFilters
      );
    } catch (error: any) {
      console.error(error);
      const apiMessage = extractApiErrorMessage(error, 'Kullanıcı eklenirken bir hata oluştu.');
      const normalizedMessage = normalizeCreateUserErrorMessage(error, apiMessage);
      openNotification('error', 'Hata', normalizedMessage);
    } finally {
      setCreateSubmitting(false);
    }
  };

  // Tablo kolonları
  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
    },
    {
      title: 'Ad Soyad',
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: true,
      render: (value, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
      sorter: true,
    },
    {
      title: 'Telefon',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (value: string) => <PhoneNumberDisplay value={value} emptyText='' />,
    },
    {
      title: 'Rol',
      key: 'roles',
      render: (_, record) => {
        if (!record.roles || record.roles.length === 0) return '—';
        const roleNames = record.roles.map((r: any) => {
          return r.title;
        });
        return roleNames.join(', ');
      },
    },
    {
      title: 'Katılma Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      defaultSortOrder: activeTabKey === 'active' ? 'descend' : undefined,
      render: (createdAt: string) => moment(createdAt).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Son Giriş Tarihi',
      dataIndex: 'lastSuccessfulLogin',
      key: 'lastSuccessfulLogin',
      sorter: true,
      render: (val: string) => {
        if (!val) {
          return <Tag color="default">Henüz giriş yapmamış</Tag>;
        }
        return moment(val).format('DD/MM/YYYY HH:mm');
      },
    },
    {
      title: 'İşlem',
      key: 'action',
      render: (_, record) => {
        const userIsAdmin = record.roles?.some((r: any) => r.name === 'ADMIN');
        const canManageRecord = (userIsAdmin && isCurrentUserAdmin) || !userIsAdmin;
        if (canManageRecord || canLoginToApplication) {
          const isCorrectHostname = isApplicationLoginHost();
          const anotherLoginIsLoading = loginAccountId !== null && loginAccountId !== record.id;
          const applicationLoginDisabled = !record.status || !isCorrectHostname || anotherLoginIsLoading;
          const applicationLoginTooltip = !record.status
            ? 'Pasif kullanıcılar için uygulamaya giriş yapılamaz.'
            : !isCorrectHostname
              ? 'Bu işlem yalnızca app.kontrolx.com.tr üzerinde kullanılabilir.'
              : anotherLoginIsLoading
                ? 'Başka bir kullanıcı için giriş işlemi devam ediyor.'
                : 'Uygulamaya Giriş';
          return (
            <div style={{ display: 'flex', gap: 8 }}>
              {canManageRecord && (
                <>
                  <Button
                    shape="circle"
                    icon={<EditOutlined />}
                    className="icon-button"
                    onClick={() => handleDetail(record.id)}
                  />
                  {record.status ? (
                    <Button
                      shape="circle"
                      className="icon-button"
                      onClick={() => openDeactivateModal(record)}
                      icon={<PoweroffOutlined style={{ color: 'green' }} />}
                    />
                  ) : (
                    <Popconfirm
                      placement="left"
                      title="Bu kullanıcıyı aktif hale getirmek istiyor musunuz?"
                      onConfirm={() => toggleUserStatus(record.id, record.status)}
                      okText="Evet"
                      cancelText="Hayır"
                    >
                      <Button
                        shape="circle"
                        className="icon-button"
                        icon={<PoweroffOutlined style={{ color: 'red' }} />}
                      />
                    </Popconfirm>
                  )}

                  {isCurrentUserAdmin && (
                    <Button
                      shape="circle"
                      className="icon-button"
                      icon={<UserSwitchOutlined />}
                      onClick={() => openRoleChangeModal(record)}
                    />
                  )}
                </>
              )}
              {canLoginToApplication && (
                <Tooltip title={applicationLoginTooltip}>
                  <span>
                    <Button
                      shape="circle"
                      className="icon-button"
                      aria-label="Uygulamaya Giriş"
                      disabled={applicationLoginDisabled}
                      icon={<LoginOutlined />}
                      loading={loginAccountId === record.id}
                      onClick={() => loginToApplication(record.id)}
                    />
                  </span>
                </Tooltip>
              )}
            </div>
          );
        }

        return null;
      },
    }
  ];

  if (activeTabKey === 'inactive') {
    columns.splice(columns.length - 1, 0, {
      title: 'Pasife Alma Tarihi',
      dataIndex: 'deactivatedAt',
      key: 'deactivatedAt',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (value: string | null | undefined, record: any) => {
        const text = value ? moment(value).format('DD/MM/YYYY HH:mm') : '-';
        const note = record?.deactivationNote || record?.deactivatedNote || record?.deactivateNote || record?.statusNote || record?.note;
        return note ? (
          <Tooltip title={note}>
            <span>{text}</span>
          </Tooltip>
        ) : (
          <span>{text}</span>
        );
      },
    });
  }

  const renderTabContent = (title: string, count: number, statusKey: 'active' | 'inactive') => (
    <Card className='br-30px mb-120px'>
      <Row gutter={[16, 16]} align='middle' justify='space-between' className='mb-20px'>
        <Col xs={24} sm={12} lg={16}>
          <Title level={4} style={{ margin: 0, display: 'inline-block' }}>
            {title}
          </Title>
          <Text type='secondary' className='ms-5'>
            <strong>({count})</strong> kayıt listelendi
          </Text>
        </Col>
        <Col xs={24} sm={12} lg={8} className='d-flex justify-content-end'>
          <Space>
            <Input
              allowClear
              placeholder='Ara'
              prefix={<SearchOutlined style={{ color: '#b8b9bc' }} />}
              className='d-input d-input-form d-input-filter'
              style={{ width: 220 }}
              onChange={handleSearchChange}
            />
            <Button
              onClick={() => openFilterDrawer(statusKey)}
              type="primary"
            >
              Filtrele
            </Button>
            <Button type="primary" onClick={() => setCreateDrawerVisible(true)}>
              Kullanıcı Ekle
            </Button>
              <Tooltip title="Dışa Aktar">
              <Button
                shape='circle'
                className='excel-icon-button'
                onClick={() => handleExportExcel(statusKey)}
                aria-label='Excel indir'
              >
                <FileExcelOutlined />
              </Button>
            </Tooltip>
          </Space>
        </Col>
      </Row>

      <Table
        scroll={{ x: 768 }}
        loading={loading}
        columns={columns}
        dataSource={users}
      pagination={{
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: pagination.total,
        showSizeChanger: true,
        pageSizeOptions: ['50', '100', '200', '500'],
        defaultPageSize: 50,
      }}
        onChange={handleTableChange}
        rowKey='id'
      />
    </Card>
  );

  return (
    <>
      <Tabs
        className='custom-tabs'
        activeKey={activeTabKey}
        onChange={handleTabChange}
        tabBarStyle={{ borderBottom: 'none' }}
      >
        <TabPane
          tab={
            <Button
              type={activeTabKey === 'active' ? 'primary' : 'default'}
              className='d-button tab-button'
            >
              Aktif Kullanıcılar
            </Button>
          }
          key='active'
        >
          {renderTabContent('Aktif Kullanıcılar Listesi', activeCount, 'active')}
        </TabPane>
        <TabPane
          tab={
            <Button
              type={activeTabKey === 'inactive' ? 'primary' : 'default'}
              className='d-button tab-button'
            >
              Pasif Kullanıcılar
            </Button>
          }
          key='inactive'
        >
          {renderTabContent('Pasif Kullanıcılar Listesi', inactiveCount, 'inactive')}
        </TabPane>
      </Tabs>

      <Drawer
        title="Kullanıcı Ekle"
        placement='right'
        open={createDrawerVisible}
        onClose={closeCreateDrawer}
        width={460}
        destroyOnClose
      >
        <Form layout="vertical" form={createUserForm} onFinish={handleCreateUser}>
          <Form.Item name="firstName" label="Ad" rules={[{ required: true, message: 'Lütfen adınızı giriniz!' }]}>
            <Input placeholder="Ad" />
          </Form.Item>
          <Form.Item name="lastName" label="Soyad" rules={[{ required: true, message: 'Lütfen soyadınızı giriniz!' }]}>
            <Input placeholder="Soyad" />
          </Form.Item>
          <Form.Item
            name="email"
            label="E-posta"
            rules={[
              { required: true, message: 'Lütfen e-posta adresinizi giriniz!' },
              { type: 'email', message: 'Lütfen geçerli bir e-posta adresi giriniz!' },
            ]}
          >
            <Input placeholder="E-posta" />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Telefon"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (!/^[5][0-9]{9}$/.test(value)) {
                    return Promise.reject(new Error('Telefon 5 ile başlamalı ve 10 rakamdan oluşmalıdır.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              addonBefore="+90"
              maxLength={10}
              placeholder="Telefon"
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name="password"
            label="Şifre"
            rules={[
              { required: true, message: 'Lütfen şifrenizi giriniz!' },
              { min: 8, message: 'Şifre en az 8 karakter olmalıdır!' },
            ]}
          >
            <Input.Password placeholder="Şifre" />
          </Form.Item>
          <Form.Item
            name="passwordRepeat"
            label="Şifre Tekrarı"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Lütfen şifrenizi tekrar giriniz!' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Şifreler eşleşmiyor!'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Şifre Tekrarı" />
          </Form.Item>
          <Form.Item name="roleName" label="Rolü" rules={[{ required: true, message: 'Lütfen rol seçiniz!' }]}>
            <Select placeholder="Rol Seçiniz" options={createRoleOptions} />
          </Form.Item>
          <Row justify="end">
            <Space>
              <Button onClick={closeCreateDrawer}>İptal</Button>
              <Button type="primary" htmlType="submit" loading={createSubmitting}>
                Kaydet
              </Button>
            </Space>
          </Row>
        </Form>
      </Drawer>

      <Drawer
        title="Filtreler"
        placement='right'
        open={filterDrawerVisible}
        onClose={closeFilterDrawer}
        width={500}
        destroyOnClose
      >
        <Space direction='vertical' size={16} style={{ width: '100%' }}>
          <div>
            <Text strong>Rol</Text>
            <Select
              allowClear
              placeholder='Rol seçin'
              options={dynamicRoleOptions}
              value={drawerFilters.role}
              onChange={(value) =>
                setDrawerFilters((prev) => ({ ...prev, role: value || undefined }))
              }
              style={{ width: '100%' }}
            />
          </div>

          {drawerTabKey === 'inactive' && (
            <div>
              <Text strong>Pasife Alma Tarihi</Text>
              <RangePicker
                allowClear
                value={drawerFilters.deactivatedRange ?? undefined}
                onChange={(values) => setDrawerRange('deactivatedRange', values)}
                style={{ width: '100%' }}
                format='DD/MM/YYYY'
              />
            </div>
          )}

          <div>
            <Text strong>Son Giriş Tarihi</Text>
            <RangePicker
              allowClear
              value={drawerFilters.lastLoginRange ?? undefined}
              onChange={(values) => setDrawerRange('lastLoginRange', values)}
              style={{ width: '100%' }}
              format='DD/MM/YYYY'
            />
          </div>

          <div>
            <Text strong>Katılma Tarihi</Text>
            <RangePicker
              allowClear
              value={drawerFilters.createdRange ?? undefined}
              onChange={(values) => setDrawerRange('createdRange', values)}
              style={{ width: '100%' }}
              format='DD/MM/YYYY'
            />
          </div>
        </Space>

        <Space style={{ marginTop: 24, width: '100%', justifyContent: 'end' }}>
          <Button onClick={resetDrawerFilters}>Temizle</Button>
          <Button type='primary' onClick={applyDrawerFilters}>
            Uygula
          </Button>
        </Space>
      </Drawer>

      <Modal
        title="Kullanıcıyı Pasif Hale Getir"
        visible={deactivateModalVisible}
        onOk={handleDeactivateConfirm}
        onCancel={handleDeactivateCancel}
        okText="Onayla"
        cancelText="Vazgeç"
        okButtonProps={{ disabled: !deactivateNoteText.trim() }}
      >
        <p>Bu kullanıcıyı pasif hale getirmek istediğinize emin misiniz?</p>
        <Input.TextArea
          placeholder="Gerekçesini giriniz"
          value={deactivateNoteText}
          onChange={(event) => {
            if (event.target.value.length <= 255) {
              setDeactivateNoteText(event.target.value);
            }
          }}
          status={!deactivateNoteText.trim() ? "error" : undefined}
          rows={3} // 4 satır gösterir
          maxLength={255} // varchar(255) ile uyumlu
        />
        {!deactivateNoteText.trim() && (
          <Text type="danger">Gerekçe alanı zorunludur.</Text>
        )}
      </Modal>

      <Modal
        title="Rolü Değiştir"
        visible={roleChangeModalVisible}
        onOk={handleRoleChangeSave}
        onCancel={handleRoleChangeCancel}
        okText="Kaydet"
        cancelText="İptal"
      >
        {selectedUser && (
          <div>
            <p>
              <strong>Kullanıcı:</strong> {selectedUser.firstName} {selectedUser.lastName}
            </p>
            <Select
              style={{ width: '100%' }}
              value={newRole}
              onChange={(val) => setNewRole(val)}
              options={dynamicRoleOptions}
            />
          </div>
        )}
      </Modal>
    </>
  );
};
